import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, ArrowRight, Clock, CheckCircle2, XCircle, Sparkles, Info } from 'lucide-react';
import CustomerAccountLayout from '../../components/CustomerAccountLayout';
import RedeemGiftCardForm from '../../components/RedeemGiftCardForm';

/* ─────────────────────────────────────────────────────────────────────
 * Customer Account → My Rewards (rebuilt Feb 2026 to replace the
 * legacy tier/milestone/food-catalog experience with the simpler
 * Gift Card Credit balance + Redeem-request flow per the new spec).
 *
 * Display rules:
 *   • Header label MUST be "🎁 Gift Card Credits Available" — not "$25".
 *   • Show BOTH Points and Gift Card Credits ($) side by side so users
 *     never confuse the balance with cash.
 *   • Available / Pending Review / Lifetime Redeemed buckets shown.
 *   • "Redeem Gift Card Credits" opens the request form modal.
 *   • Gift Card History lists earnings (Restaurant Referral, Friend
 *     Referral, Social Share) AND redemptions (Redeemed at <restaurant>).
 *   • Status workflow: Pending Review · Approved · Declined · Completed.
 * ─────────────────────────────────────────────────────────────────── */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const STATUS_PILLS = {
  pending_review: { label: 'Pending Review', tone: 'bg-amber-500/15 text-amber-300 border-amber-500/30', Icon: Clock },
  approved:       { label: 'Approved',       tone: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', Icon: CheckCircle2 },
  completed:      { label: 'Delivered',      tone: 'bg-sky-500/15 text-sky-300 border-sky-500/30', Icon: CheckCircle2 },
  declined:       { label: 'Rejected',       tone: 'bg-rose-500/15 text-rose-300 border-rose-500/30', Icon: XCircle },
};

const KIND_LABEL = {
  social_share: 'Social Share',
  referral_signup: 'Friend Referral',
  restaurant_referral: 'Restaurant Referral',
  // Old reward kinds keep displaying gracefully so historical rows
  // still render a sensible label after the Feb 2026 redesign.
  referral_first_order: 'Friend First Order',
  redemption: 'Redemption',
};

const AccountRewards = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeemOpen, setRedeemOpen] = useState(false);

  const reload = () => setReloadKey((k) => k + 1);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/customers/me/rewards`,             { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/customers/me/redemption-requests`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (cancelled) return;
        setData(r1.data);
        setRequests(r2.data?.requests || []);
      } catch {
        if (cancelled) return;
        toast.error('Could not load rewards');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token, reloadKey]);

  const s = data?.summary || {};
  const cfg = data?.config || {};
  const points = Number(s.total_points || 0);
  const ptsPerDollar = Number(cfg.points_per_dollar_gift_credit || 2);
  const availableCents = Number(s.gift_credit_cents || 0);
  const pendingCents = Number(s.pending_redemption_cents || 0);
  const lifetimeRedeemed = Number(s.redeemed_lifetime_cents || 0);
  // Lifetime Earned = everything ever credited to this account (current
  // available + currently held in pending + already redeemed). Server
  // computes the same value; we fall back to the local sum for safety.
  const lifetimeEarned = Number(
    s.lifetime_earned_cents ?? (availableCents + pendingCents + lifetimeRedeemed),
  );
  const history = (data?.history || []).slice(0, 50);
  const processingHours = Number(cfg.redemption_processing_hours || 72);

  return (
    <CustomerAccountLayout
      currentKey="rewards"
      title="My Rewards"
      subtitle="Your Gift Card Credit balance, redemption requests, and earning history."
    >
      {loading ? (
        <div className="text-white/45 text-[14px]" data-testid="rewards-loading">Loading…</div>
      ) : (
        <>
          {/* ─── Gift Card Credits hero (4-tile balance) ─── */}
          <div
            data-testid="rewards-balance-card"
            className="p-5 sm:p-7 rounded-3xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/[0.08] via-zinc-950 to-black"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                <Gift className="w-4 h-4" /> Gift Card Credits
              </div>
              <div
                data-testid="rewards-points-pill"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-black/30 text-[11px] font-bold text-white/75 tabular-nums"
              >
                <Sparkles className="w-3 h-3 text-yellow-300/85" />
                {points} pts
                <span className="text-white/35 font-normal">
                  · {ptsPerDollar} pts = $1.00
                </span>
              </div>
            </div>

            {/* 4-tile balance grid — Available · Pending Review ·
                Redeemed · Lifetime Earned. Each tile uses tabular-nums so
                the dollar columns line up cleanly on mobile. */}
            <div
              data-testid="rewards-balance-tiles"
              className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2.5"
            >
              <div className="p-3.5 rounded-2xl bg-yellow-500/[0.08] border border-yellow-500/30">
                <div className="text-[10px] uppercase tracking-[0.18em] text-yellow-300/85 font-bold">Available</div>
                <div className="mt-1 text-2xl sm:text-[28px] font-extrabold text-yellow-300 tabular-nums" data-testid="rewards-tile-available">
                  {money(availableCents)}
                </div>
                <div className="text-[10.5px] text-white/45 mt-0.5">Ready to request</div>
              </div>
              <div className="p-3.5 rounded-2xl bg-amber-500/[0.06] border border-amber-500/25">
                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/85 font-bold">Pending Review</div>
                <div className="mt-1 text-2xl sm:text-[28px] font-extrabold text-amber-300 tabular-nums" data-testid="rewards-tile-pending">
                  {money(pendingCents)}
                </div>
                <div className="text-[10.5px] text-white/45 mt-0.5">In review</div>
              </div>
              <div className="p-3.5 rounded-2xl bg-emerald-500/[0.06] border border-emerald-500/25">
                <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/85 font-bold">Redeemed</div>
                <div className="mt-1 text-2xl sm:text-[28px] font-extrabold text-emerald-300 tabular-nums" data-testid="rewards-tile-redeemed">
                  {money(lifetimeRedeemed)}
                </div>
                <div className="text-[10.5px] text-white/45 mt-0.5">Delivered to you</div>
              </div>
              <div className="p-3.5 rounded-2xl bg-white/[0.04] border border-white/10">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">Lifetime Earned</div>
                <div className="mt-1 text-2xl sm:text-[28px] font-extrabold text-white tabular-nums" data-testid="rewards-tile-lifetime-earned">
                  {money(lifetimeEarned)}
                </div>
                <div className="text-[10.5px] text-white/45 mt-0.5">Since account creation</div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setRedeemOpen(true)}
              disabled={availableCents < 100}
              data-testid="rewards-redeem-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Redeem Gift Card Credits <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-2.5 text-[12px] text-white/55 flex items-center gap-1.5 justify-center">
              <Info className="w-3.5 h-3.5" /> Reward requests are reviewed within {processingHours} hours.
            </p>
          </div>

          {/* iter87 (Jun 2026): the mid-page "View Full Statement" card
              was moved to its own sidebar entry (Statement, between
              Referrals and Settings) — customers couldn't spot it when
              it lived in the middle column. */}

          {/* ─── Redemption requests list ─── */}
          {requests.length > 0 && (
            <div className="mt-6" data-testid="rewards-redemption-requests">
              <h2 className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-bold mb-3">Redemption Requests</h2>
              <ul className="divide-y divide-white/[0.05] rounded-2xl border border-white/8 bg-white/[0.015] overflow-hidden">
                {requests.map((r) => {
                  const pill = STATUS_PILLS[r.status] || STATUS_PILLS.pending_review;
                  const PIcon = pill.Icon;
                  return (
                    <li key={r.id} className="p-3.5 flex items-center justify-between gap-3" data-testid={`rewards-request-${r.id}`}>
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-semibold text-white truncate">
                          {money(r.requested_cents)}
                          {r.restaurant_name ? <span className="text-white/55 font-normal"> · for {r.restaurant_name}</span> : null}
                        </div>
                        <div className="text-[11.5px] text-white/45">
                          Submitted {new Date(r.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10.5px] font-bold uppercase tracking-wider ${pill.tone}`}>
                        <PIcon className="w-3 h-3" /> {pill.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* ─── Earning history ─── */}
          <div className="mt-7" data-testid="rewards-history-block">
            <h2 className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-bold mb-3 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-yellow-300/85" /> Gift Card History
            </h2>
            {history.length === 0 ? (
              <div className="p-6 rounded-2xl border border-white/8 bg-white/[0.015] text-center text-white/55 text-[13.5px]">
                Start earning by inviting a friend or sharing on social.
              </div>
            ) : (
              <ul className="divide-y divide-white/[0.05] rounded-2xl border border-white/8 bg-white/[0.015] overflow-hidden">
                {history.map((tx) => {
                  const dollars = ((Math.abs(tx.amount || 0) * 0.5)).toFixed(2);
                  const isPositive = (tx.amount || 0) >= 0;
                  return (
                    <li key={tx.id} className="p-3.5 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-semibold text-white truncate">
                          {KIND_LABEL[tx.kind] || tx.kind}
                          {tx.metadata?.network ? <span className="text-white/55 font-normal"> · {tx.metadata.network}</span> : null}
                        </div>
                        <div className="text-[11.5px] text-white/45">
                          {tx.created_at ? new Date(tx.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                        </div>
                      </div>
                      <div className={`text-[13px] font-extrabold tabular-nums ${isPositive ? 'text-emerald-300' : 'text-rose-300'}`}>
                        {isPositive ? '+' : '−'}${dollars}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* ─── Redeem modal ─── */}
          {redeemOpen && (
            <RedeemGiftCardForm
              token={token}
              availableCents={availableCents}
              pendingCents={pendingCents}
              points={points}
              ptsPerDollar={ptsPerDollar}
              processingHours={processingHours}
              defaultEmail={data?.summary?.email || ''}
              defaultName={data?.summary?.name || ''}
              defaultPhone={data?.summary?.phone || ''}
              onClose={() => setRedeemOpen(false)}
              onSubmitted={() => {
                setRedeemOpen(false);
                reload();
              }}
            />
          )}
        </>
      )}
    </CustomerAccountLayout>
  );
};

export default AccountRewards;
